// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AutoDeliveryLink from "@guestsupply/App/Components/Product/AutoDeliveryLink";
import ProductActions, {
    ProductActionsProps,
    ProductActionsStyle,
} from "@guestsupply/App/Components/Product/ProductActions";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@guestsupply/App/Components/Product/ProductGridContainer";
import xItemReplacements from "@guestsupply/App/Handlers/XNG/ItemReplacements";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import ProductPrice, { ProductPriceStyles } from "@guestsupply/Overrides/Components/ProductPrice";
import { parseBoolean, xScreen } from "@guestsupply/XDL/Utilities";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const productsWithReplacementsString = (getProductListDataView(state) as any)?.properties?.productsWithReplacements;

    return {
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isAuthenticated: xIsAuthenticated(state),
        isPunchout: getSettingsCollection(state).customSettings.isPunchout,
        cannotProp: getSession(state).properties?.cannotCreateRecurringOrders,
        hideSignInLink: getSession(state).isAuthenticated,
        productsWithReplacements: (productsWithReplacementsString && JSON.parse(productsWithReplacementsString)) || [],
    };
};
const mapDispatchToProps = {
    xItemReplacements,
};
export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    // XNG-Changes:
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    displayAutoReorderAsButton?: boolean;
    isPdp?: boolean;
    showAutoDelivery?: boolean;
    isPlp?: boolean;
    showReplacements?: boolean;
}

type Props = ProductPriceAndActionsProps &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
    availabilityWrapper?: InjectableCss;
    signInLink?: LinkPresentationProps;
    availableOptionsLink?: LinkPresentationProps;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    currentUserIsGuest,
    isAuthenticated,
    cannotProp,
    isPunchout,
    displayAutoReorderAsButton,
    isPdp = false,
    hideSignInLink,
    showAutoDelivery = true,
    isPlp = false,
    productsWithReplacements,
    xItemReplacements,
    showReplacements,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const history = useHistory();

    const signInClickHandler = () => {
        if (!signInUrl) {
            history.push("/MyAccount/SignIn");
        } else {
            history.push(`${signInUrl}`);
        }
    };
    const cannotAutoDeliver = () => {
        return cannotProp && parseBoolean(cannotProp) && isPunchout;
    };
    const handleitemReplacementsClick = () => {
        // adding setTimeout to solve scroll issue, where it doesn't always apply scroll behaviour
        setTimeout(() => {
            xItemReplacements({
                id: productContext.product.id,
                value: {
                    productItemReplacement: true,
                },
            });
        }, 250);
    };
    const subscriptionFlag = productContext.product.properties?.subscription_Flag?.toLocaleLowerCase();

    const enableAutoDelivery =
        !currentUserIsGuest && isAuthenticated && subscriptionFlag === "true" && !cannotAutoDeliver();

    const hasItemReplacements = productsWithReplacements.includes(productContext?.product?.id);
    return (
        <ProductGridContainer extendedStyles={styles.productGridContainer}>
            {showPrice && (
                <ProductGridContainer.Price>
                    <ProductPrice
                        showLabel={false}
                        showInvalidPriceMessage={false}
                        product={productContext}
                        extendedStyles={styles.productPriceStyles}
                        isPdp={isPdp}
                    />
                    {isPdp && !hideSignInLink && (
                        <Link
                            onClick={signInClickHandler}
                            {...styles.signInLink}
                            aria-label="Sign In To See Your Price"
                        >
                            {translate("Sign In To See Your Price")}
                        </Link>
                    )}
                </ProductGridContainer.Price>
            )}
            {canSeePrices && (showAvailability || enableAutoDelivery || showReplacements) && (
                <ProductGridContainer.Availability>
                    <StyledWrapper {...styles.availabilityWrapper}>
                        {showAvailability && (
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityStyles}
                                {...{ hideLink: hideAvailabilityLink }}
                            />
                        )}
                        {showAutoDelivery && enableAutoDelivery && !isPdp && (
                            <AutoDeliveryLink displayAsButton={displayAutoReorderAsButton} />
                        )}
                    </StyledWrapper>
                    {isPlp && hasItemReplacements && showReplacements && (
                        <Link
                            href={productContext.productInfo.productDetailPath}
                            onClick={handleitemReplacementsClick}
                            {...styles.availableOptionsLink}
                        >
                            {translate("Available Options")}
                        </Link>
                    )}
                </ProductGridContainer.Availability>
            )}
            {canSeePrices && showAddToCartActions && (
                <ProductGridContainer.AddToCartActions>
                    <ProductActions
                        {...productActionsProps}
                        onAddToCartClick={onAddToCartClick}
                        showDisabled={showDisabled}
                        iconOnly={showAddToCartIcon}
                        extendedStyles={styles.productActionsStyles}
                        isPdp={isPdp}
                    />
                </ProductGridContainer.AddToCartActions>
            )}

            {canSeePrices && showAutoDelivery && enableAutoDelivery && isPdp && (
                <ProductGridContainer.AutoDeliveryButton>
                    <AutoDeliveryLink displayAsButton={displayAutoReorderAsButton} />
                </ProductGridContainer.AutoDeliveryButton>
            )}
            {canSeePrices && (showAddToList || showCompare) && (
                <ProductGridContainer.AddToList>
                    <ProductListProductImage
                        showImage={false}
                        showCompare={showCompare}
                        extendedStyles={styles.productListProductImageStyles}
                    />
                    {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                </ProductGridContainer.AddToList>
            )}
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductPriceAndActions));
